import React from 'react';
import { parseCurrencyToNumber } from '../../utils/formatters';
import AnimatedNumberDisplay from '../ui/AnimatedNumberDisplay';
import { TrendingUp, Award, Zap } from 'lucide-react';
import { motion } from 'framer-motion';

interface MultiplierCardProps {
  chartId: string;
  summaryData: Array<{
    label: string;
    value: string;
    color: string;
  }>;
  currencyFormat?: 'MXN' | 'USD';
  className?: string;
  validChartIds?: string[];
}

const MultiplierCard: React.FC<MultiplierCardProps> = ({
  chartId,
  summaryData,
  currencyFormat = 'MXN',
  className = '',
  validChartIds
}) => {
  // If validChartIds is provided, check if current chartId is included
  if (validChartIds && !validChartIds.includes(chartId)) {
    return null;
  }

  // Skip calculation for non-comparable data
  const nonComparableLabels = [
    'Inversión Inicial', 'Meta Mensual', 'Diferencia', 'Progreso', 
    'Certificados', 'Iniciales', 'Por Reinversión', 'Total Final',
    'Flujo Final', 'Fondo Reinversión', 'Año Recuperación', 'CAGR'
  ];

  // Find Cosecha Capital and the second highest value
  let cosechaCapitalValue = 0;
  let secondHighestValue = 0;
  let secondHighestLabel = '';
  let hasComparableData = false;

  // First pass: find Cosecha Capital value
  for (const item of summaryData) {
    if (item.label.includes('Cosecha Capital')) {
      cosechaCapitalValue = parseCurrencyToNumber(item.value);
      hasComparableData = true;
      break;
    }
  }

  // Second pass: find second highest value
  for (const item of summaryData) {
    // Skip non-comparable labels and Cosecha Capital itself
    if (nonComparableLabels.some(label => item.label.includes(label)) || 
        item.label.includes('Cosecha Capital')) {
      continue;
    }
    
    const value = parseCurrencyToNumber(item.value);
    if (value > secondHighestValue) {
      secondHighestValue = value;
      secondHighestLabel = item.label;
      hasComparableData = true;
    }
  }

  // Calculate multiplier
  const multiplier = secondHighestValue > 0 ? cosechaCapitalValue / secondHighestValue : 0;
  
  // Only show if there's a significant advantage (>= 1.1x) and we have comparable data
  if (multiplier < 1.1 || !hasComparableData || secondHighestLabel === '') {
    return null;
  }

  // Determine color class based on multiplier value
  const getColorClass = () => {
    if (multiplier >= 3) return 'from-purple-500 to-purple-600';
    if (multiplier >= 2) return 'from-blue-500 to-blue-600';
    return 'from-green-500 to-green-600';
  };

  // Determine icon based on multiplier value
  const getIcon = () => {
    if (multiplier >= 3) return <Zap className="w-6 h-6 text-white" />;
    if (multiplier >= 2) return <Award className="w-6 h-6 text-white" />;
    return <TrendingUp className="w-6 h-6 text-white" />;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className={`mt-6 bg-gradient-to-br ${getColorClass()} rounded-2xl shadow-lg p-6 text-white ${className}`}
    >
      <div className="flex items-center gap-4 mb-4">
        <motion.div 
          className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center"
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          {getIcon()}
        </motion.div>
        <div>
          <h3 className="text-lg font-bold">Factor Multiplicador</h3>
          <p className="text-sm text-white/80">Cosecha Capital vs {secondHighestLabel}</p>
        </div>
      </div>
      
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-white/80 mb-1">Cosecha Capital es</p>
          <div className="text-4xl font-bold relative overflow-hidden">
            <AnimatedNumberDisplay
              value={multiplier}
              currencyFormat="MXN"
              duration={1.6}
              staggerDelay={0.12}
              isCurrency={false}
            />x
          </div>
          <p className="text-sm text-white/80 mt-1">mejor que {secondHighestLabel}</p>
        </div>
        
        <div className="text-right">
          <p className="text-sm text-white/80 mb-1">Esto significa</p>
          <div className="text-xl font-bold relative overflow-hidden">
            <AnimatedNumberDisplay
              value={(multiplier - 1) * 100}
              currencyFormat="MXN"
              duration={1.4}
              staggerDelay={0.1}
              isCurrency={false}
            />% más
          </div>
          <p className="text-sm text-white/80 mt-1">de rendimiento</p>
        </div>
      </div>
    </motion.div>
  );
};

export default MultiplierCard;